import Layout from "../../../../components/Layout";
import useStyles from "../../styles";
import Button from "../../../../components/Button";
import BreadCrumb from "../../../../components/BreadCrumb";
import { useState, useEffect } from "react";
import { httprequest } from "../../../../data/api";
import { Icon } from "@iconify/react";
import { Grid, Menu, MenuItem, IconButton, Pagination } from "@mui/material";
import SelectGroup from "../../../../components/FormComponent/SelectGroup";
import InputGroup from "../../../../components/FormComponent/InputGroup";
import Modal from "../../../../components/Modal";
import BulkQuestionImport from "../../../../components/BulkQuestionImport";
import { questionDisplayIndex } from "../../../../utils/questionDisplayIndex";
import { toast } from "react-toastify";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import questionCsvUrl from "../../../../assets/sample/question.csv";
import questionsXlsxUrl from "../../../../assets/sample/questions.xlsx";
import { TableSearch } from "../../../../components/Table/TableActions";

const QUESTION_BANK_ENDPOINT = "/api/v1/questions";
const QUESTIONS_PER_PAGE = 21;

const questionTypeOptions = [
  {
    value: "multiple_choice",
    text: "Multiple Choice",
    icon: <Icon icon="mdi:radiobox-marked" width="25" height="25" />
  },
  {
    value: "true_false",
    text: "True / False",
    icon: <Icon icon="mdi:toggle-switch-outline" width="25" height="25" />
  },
  {
    value: "checkbox",
    text: "Checkbox",
    icon: <Icon icon="ci:checkbox-check" width="25" height="25" />
  }
];

const QuestionBank = () => {
  const styles = useStyles();

  const [questions, setQuestions] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedExamType, setSelectedExamType] = useState("");
  const [examTypesData, setExamTypesData] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [totalServerPages, setTotalServerPages] = useState(1);
  const [isFetchingQuestions, setIsFetchingQuestions] = useState(false);

  const [isAddQuestionModalOpen, setIsAddQuestionModalOpen] = useState(false);
  const [questionArr, setQuestionArr] = useState([]);
  const [isEditQuestionsModalOpen, setIsEditQuestionsModalOpen] = useState(false);
  const [editQuestionArr, setEditQuestionArr] = useState([]);
  const [isBatchImportModalOpen, setIsBatchImportModalOpen] = useState(false);
  const [batchFileType, setBatchFileType] = useState("csv");
  const [isBatchImportLoading, setIsBatchImportLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteQuestionIndex, setDeleteQuestionIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuQueIndex, setMenuQueIndex] = useState(null);
  const open = Boolean(anchorEl);
  const [editAnchorEl, setEditAnchorEl] = useState(null);
  const [editMenuQueIndex, setEditMenuQueIndex] = useState(null);
  const editMenuOpen = Boolean(editAnchorEl);

  const getExamTypes = async () => {
    try {
      const res = await httprequest.get("/api/v1/exam-types");
      setExamTypesData(res?.data?.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch exam types", err);
    }
  };

  const fetchQuestions = async () => {
    setIsFetchingQuestions(true);
    try {
      const params = { limit: QUESTIONS_PER_PAGE, page: currentPage };
      if (selectedExamType) params.exam_type = selectedExamType;
      if (selectedSubject) params.subject_id = selectedSubject;
      if (selectedYear) params.year = selectedYear;
      if (searchKeyword.trim()) params.search = searchKeyword.trim();

      const res = await httprequest.get(QUESTION_BANK_ENDPOINT, { params });
      const responseData = res?.data?.data;
      const items = responseData?.items || (Array.isArray(responseData) ? responseData : []);
      setQuestions(items);
      setTotalServerPages(responseData?.total_pages || 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data || "Failed to load questions");
    } finally {
      setIsFetchingQuestions(false);
    }
  };

  const handleExamTypeChange = (e) => {
    setSelectedExamType(e.target.value);
    setSelectedSubject("");
    setSelectedYear("");
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
    setSelectedYear("");
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleSearchStudent = (e) => {
    setSearchKeyword(e.target.value);
  };

  const paginatedQuestions = questions;
  const totalPages = totalServerPages;

  useEffect(() => {
    getExamTypes();
  }, []);

  useEffect(() => {
    const fetchSubjectsData = async () => {
      if (!selectedExamType) {
        setSubjects([]);

        return;
      }
      try {
        const res = await httprequest.get(`/api/v1/exam-types/${selectedExamType}/subjects`);
        if (res?.data?.data) {
          setSubjects(res.data.data);
        }
      } catch (err) {
        setSubjects([]);
      }
    };
    fetchSubjectsData();
  }, [selectedExamType]);

  useEffect(() => {
    const fetchYearsData = async () => {
      if (!selectedSubject) {
        setYears([]);

        return;
      }
      try {
        const res = await httprequest.get(`/api/v1/subjects/${selectedSubject}/years${selectedExamType ? `?exam_type=${selectedExamType}` : ""}`);
        if (res?.data?.data) {
          setYears(res.data.data);
        }
      } catch (err) {
        setYears([]);
      }
    };
    fetchYearsData();
  }, [selectedSubject, selectedExamType]);

  useEffect(() => {
    fetchQuestions();
  }, [selectedExamType, selectedSubject, selectedYear, currentPage, searchKeyword]);

  const handlePageChange = (_event, page) => {
    setCurrentPage(page);
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteModalOpen = (index) => {
    setDeleteQuestionIndex(index);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setDeleteQuestionIndex(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteQuestionIndex !== null) {
      const q = questions[deleteQuestionIndex];
      try {
        await httprequest.post(`${QUESTION_BANK_ENDPOINT}/questions/delete`, { ids: [q.id] });
        toast.success("Question deleted successfully");
        fetchQuestions();
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to delete question");
      }
    }
    handleDeleteModalClose();
  };

  // eslint-disable-next-line no-unused-vars
  const handleOpenAddQuestionModal = () => {
    setQuestionArr([{
      question_text: "",
      type: "",
      options: [""],
      answer: [],
      grade_point: "",
      add_to_bank: true,
      required: false,
      question_index: 1
    }]);
    setIsAddQuestionModalOpen(true);
  };

  const handleQueChange = (e, queInd) => {
    const { name, value } = e.target;
    const newArr = [...questionArr];
    newArr[queInd] = { ...newArr[queInd], [name]: value };
    setQuestionArr(newArr);
  };

  const handleQueTypeChange = (e, queInd) => {
    const { value } = e.target;
    const newArr = [...questionArr];
    if (value === "true_false") {
      newArr[queInd] = { ...newArr[queInd], type: value, options: ["True", "False"], answer: [] };
    } else {
      newArr[queInd] = {
        ...newArr[queInd],
        type: value,
        options: newArr[queInd].options?.length > 0 ? newArr[queInd].options : [""]
      };
    }
    setQuestionArr(newArr);
  };

  const handleQuePointChange = (e, queInd) => {
    const newArr = [...questionArr];
    newArr[queInd] = { ...newArr[queInd], grade_point: e.target.value };
    setQuestionArr(newArr);
  };

  const handleMultipleChoiceChange = (e, optInd, queInd) => {
    const newArr = [...questionArr];
    const newOptions = [...newArr[queInd].options];
    newOptions[optInd] = e.target.value;
    newArr[queInd] = { ...newArr[queInd], options: newOptions };
    setQuestionArr(newArr);
  };

  const handleCorrectChoiceChange = (e, optInd, queInd) => {
    const newArr = [...questionArr];
    if (newArr[queInd].type === "checkbox") {
      const prev = newArr[queInd].answer || [];
      newArr[queInd] = {
        ...newArr[queInd],
        answer: e.target.checked ? [...prev, optInd] : prev.filter((a) => a !== optInd)
      };
    } else {
      newArr[queInd] = { ...newArr[queInd], answer: [optInd] };
    }
    setQuestionArr(newArr);
  };

  const handleAdditionalOptions = (queInd) => {
    const newArr = [...questionArr];
    newArr[queInd] = { ...newArr[queInd], options: [...newArr[queInd].options, ""] };
    setQuestionArr(newArr);
  };

  const removeAdditionalChoice = (optInd, queInd) => {
    const newArr = [...questionArr];
    const newOptions = newArr[queInd].options.filter((_, i) => i !== optInd);
    newArr[queInd] = { ...newArr[queInd], options: newOptions.length ? newOptions : [""] };
    setQuestionArr(newArr);
  };

  const handleAdditionalQuestion = () => {
    setQuestionArr((prev) => [...prev, {
      question_text: "",
      type: "",
      options: [""],
      answer: [],
      grade_point: "",
      add_to_bank: true,
      required: false,
      question_index: prev.length + 1
    }]);
  };

  const removeAdditionalQue = (queInd) => {
    setQuestionArr((prev) => prev.filter((_, i) => i !== queInd));
  };

  const handleQueDescChange = (e, queInd) => {
    const newArr = [...questionArr];
    newArr[queInd] = { ...newArr[queInd], description: e.target.value };
    setQuestionArr(newArr);
  };

  const removeQueDescription = (queInd) => {
    const newArr = [...questionArr];
    const { description: _desc, ...rest } = newArr[queInd];
    void _desc;
    newArr[queInd] = rest;
    setQuestionArr(newArr);
  };

  const handleClick = (e, queInd) => {
    setAnchorEl(e.currentTarget);
    setMenuQueIndex(queInd);
  };

  const handleAdditionalQueClose = () => {
    setAnchorEl(null);
    setMenuQueIndex(null);
  };

  const toggleQueDescriptionTrue = () => {
    if (menuQueIndex != null) {
      const newArr = [...questionArr];
      newArr[menuQueIndex] = { ...newArr[menuQueIndex], description: "" };
      setQuestionArr(newArr);
    }
  };

  const handleSaveNewQuestions = async () => {
    const valid = questionArr.every((q) => q.question_text?.trim() && q.type);
    if (!valid) {
      toast.error("Please fill in all question fields");

      return;
    }
    try {
      const payload = questionArr.map((q) => ({
        ...q,
        grade_point: Number(q.grade_point) || 0,
        description: q.description ?? null
      }));
      await httprequest.post(`${QUESTION_BANK_ENDPOINT}/questions`, { questions: payload });
      setIsAddQuestionModalOpen(false);
      setQuestionArr([]);
      toast.success("Questions added to bank");
      fetchQuestions();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save questions");
    }
  };

  const handleCreateDraftQuestions = () => {
    handleSaveNewQuestions();
  };

  // eslint-disable-next-line no-unused-vars
  const handleOpenEditModal = (index) => {
    const q = questions[index];
    setEditQuestionArr([{ ...q, options: [...q.options], answer: [...q.answer] }]);
    setIsEditQuestionsModalOpen(true);
  };

  const editHandleQueChange = (e, queInd) => {
    const { name, value } = e.target;
    setEditQuestionArr((prev) => prev.map((q, i) => (i === queInd ? { ...q, [name]: value } : q)));
  };

  const editHandleQueTypeChange = (e, queInd) => {
    const { value } = e.target;
    setEditQuestionArr((prev) => prev.map((q, i) => {
      if (i !== queInd) return q;
      if (value === "true_false") {
        return { ...q, type: value, options: ["True", "False"], answer: [] };
      }

      return { ...q, type: value, options: q.options?.length > 0 ? q.options : [""] };
    }));
  };

  const editHandleQuePointChange = (e, queInd) => {
    setEditQuestionArr((prev) => prev.map((q, i) => (i === queInd ? { ...q, grade_point: e.target.value } : q)));
  };

  const editHandleMultipleChoiceChange = (e, optInd, queInd) => {
    setEditQuestionArr((prev) => prev.map((q, i) => {
      if (i !== queInd) return q;
      const newOptions = [...q.options];
      newOptions[optInd] = e.target.value;

      return { ...q, options: newOptions };
    }));
  };

  const editHandleCorrectChoiceChange = (e, optInd, queInd) => {
    setEditQuestionArr((prev) => prev.map((q, i) => {
      if (i !== queInd) return q;
      if (q.type === "checkbox") {
        const prevAnswers = q.answer || [];

        return {
          ...q,
          answer: e.target.checked ? [...prevAnswers, optInd] : prevAnswers.filter((a) => a !== optInd)
        };
      }

      return { ...q, answer: [optInd] };
    }));
  };

  const editHandleAdditionalOptions = (queInd) => {
    setEditQuestionArr((prev) => prev.map((q, i) => (i === queInd ? { ...q, options: [...q.options, ""] } : q)));
  };

  const removeEditChoice = (optInd, queInd) => {
    setEditQuestionArr((prev) => prev.map((q, i) => {
      if (i !== queInd) return q;
      const newOptions = q.options.filter((_, idx) => idx !== optInd);

      return { ...q, options: newOptions.length ? newOptions : [""] };
    }));
  };

  const editHandleQueDescChange = (e, queInd) => {
    setEditQuestionArr((prev) => prev.map((q, i) => (i === queInd ? { ...q, description: e.target.value } : q)));
  };

  const editRemoveQueDescription = (queInd) => {
    setEditQuestionArr((prev) => prev.map((q, i) => {
      if (i !== queInd) return q;
      const { description: _desc, ...rest } = q;
      void _desc;

      return rest;
    }));
  };

  const editHandleClick = (e, queInd) => {
    setEditAnchorEl(e.currentTarget);
    setEditMenuQueIndex(queInd);
  };

  const editHandleMenuClose = () => {
    setEditAnchorEl(null);
    setEditMenuQueIndex(null);
  };

  const editToggleQueDescriptionTrue = () => {
    if (editMenuQueIndex != null) {
      setEditQuestionArr((prev) => prev.map((q, i) => (i === editMenuQueIndex ? { ...q, description: "" } : q)));
    }
  };

  const handleSaveEditQuestion = async () => {
    const q = editQuestionArr[0];
    if (!q?.question_text?.trim() || !q?.type) {
      toast.error("Please fill in all question fields");

      return;
    }
    try {
      const payload = {
        question_text: q.question_text,
        type: q.type,
        options: q.options,
        answer: q.answer,
        grade_point: Number(q.grade_point) || 0,
        description: q.description ?? null
      };
      await httprequest.patch(`${QUESTION_BANK_ENDPOINT}/questions/${q.id}`, payload);
      setIsEditQuestionsModalOpen(false);
      setEditQuestionArr([]);
      toast.success("Question updated");
      fetchQuestions();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update question");
    }
  };

  const handleBatchImportModalToggle = () => {
    setIsBatchImportModalOpen(!isBatchImportModalOpen);
  };

  const getRowVal = (row, key) => {
    for (const k of Object.keys(row)) {
      if (k.trim().toLowerCase() === key.toLowerCase()) return row[k];
    }
  };

  const normalizeImportedRow = (row) => {
    const rawOptions = String(getRowVal(row, "options") ?? "").split(",").map((o) => o.trim()).filter(Boolean);
    const rawAnswer = String(getRowVal(row, "answer") ?? "");
    const answerType = String(getRowVal(row, "answer_type") ?? "multiple_choice").toLowerCase();
    const type = answerType === "checkbox" ? "checkbox" : answerType === "true_false" ? "true_false" : "multiple_choice";
    const answerIndices = rawAnswer.split(",").map((a) => a.trim()).map((a) => {
      const idx = rawOptions.findIndex((o) => o.toLowerCase() === a.toLowerCase());

      return idx >= 0 ? idx : null;
    }).filter((i) => i !== null);

    return {
      question_text: String(getRowVal(row, "questions") ?? getRowVal(row, "question") ?? ""),
      description: getRowVal(row, "description") ?? null,
      type,
      options: type === "true_false" ? ["True", "False"] : rawOptions.length > 0 ? rawOptions : [""],
      answer: answerIndices,
      grade_point: Number(getRowVal(row, "grade_point") ?? getRowVal(row, "points") ?? 0),
      add_to_bank: String(getRowVal(row, "question_bank") ?? "true").toLowerCase() !== "false",
      required: false
    };
  };

  const _sendBatchImport = async (importedData) => {
    const payload = importedData.map((q) => ({
      ...q
    }));
    try {
      await httprequest.post(`${QUESTION_BANK_ENDPOINT}/questions`, { questions: payload });
      toast.success(`${payload.length} questions imported successfully`);
      setIsBatchImportModalOpen(false);
      fetchQuestions();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to import questions");
    } finally {
      setIsBatchImportLoading(false);
    }
  };

  const handleBatchImportQuestions = (file) => {
    setIsBatchImportLoading(true);
    if (batchFileType === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const imported = results.data.map(normalizeImportedRow);
          _sendBatchImport(imported);
        },
        error: () => {
          setIsBatchImportLoading(false);
          toast.error("Failed to parse CSV file");
        }
      });
    } else {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const workbook = XLSX.read(evt.target.result, { type: "binary" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet);
          const imported = rows.map(normalizeImportedRow);
          _sendBatchImport(imported);
        } catch {
          setIsBatchImportLoading(false);
          toast.error("Failed to parse Excel file");
        }
      };
      reader.readAsBinaryString(file);
    }
  };

  function renderQuestionBuilder() {
    return (
      <>
        <div className="margin-top">
          <Grid container spacing={2}>
            {questionArr?.map((que, queInd) => (
              <>
                <Grid item lg={12} md={12} sm={12} xs={12} key={queInd}>
                  <div className={`${styles.assignmentsection}`}>
                    <Grid container spacing={2}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <div className={styles.quesaction}>
                          <label className={styles.quelabel}>Enter Question  {queInd + 1}</label>
                          <div>
                            {questionArr?.length > 1 ? (
                              <>
                                <Icon
                                  icon="material-symbols:delete-outline"
                                  width="20"
                                  height="20"
                                  className="delete-icon"
                                  onClick={() => removeAdditionalQue(queInd)}
                                />
                              </>
                            ) : null}
                          </div>
                        </div>
                        <div className={styles.textandimgmain}>
                          <div className={styles.textandimg}>
                            <input
                              type="text"
                              placeholder="Type Question"
                              name="question_text"
                              value={que?.question_text}
                              onChange={(e) => {
                                handleQueChange(e, queInd);
                              }}
                            />
                          </div>
                        </div>
                      </Grid>
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <SelectGroup
                          label="Add Answer Type"
                          required
                          children={
                            <select
                              name="type"
                              value={que?.type}
                              onChange={(e) => handleQueTypeChange(e, queInd)}
                            >
                              <option value="">--- Select ---</option>
                              {questionTypeOptions?.map((label) => (
                                <option key={label?.value} value={label?.value}>
                                  {label?.text}
                                </option>
                              ))}
                            </select>
                          }
                        />
                      </Grid>
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <InputGroup
                          className={styles.pointsInput}
                          label="Question Point"
                          inputType="number"
                          inputName="grade_point"
                          placeHolder="Enter Question Points"
                          value={que?.grade_point}
                          onChange={(e) => handleQuePointChange(e, queInd)}
                        />
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        {"description" in que ? (
                          <div className="margin-top full-width">
                            <div className=" flex gap-10 justify-items-space">
                              <label className={styles.enterque}>Question Description</label>
                              <div>
                                <div className="delete-icon" onClick={() => removeQueDescription(queInd)}>
                                  <Icon icon="ooui:close" width="20" height="20" />
                                </div>
                              </div>
                            </div>
                            <div className={styles.textandimgmain}>
                              <div className={styles.textandimg}>
                                <input
                                  type="text"
                                  placeholder="Enter Description"
                                  name="description"
                                  value={que?.description}
                                  onChange={(e) => handleQueDescChange(e, queInd)}
                                />
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </Grid>
                    </Grid>
                    {(que?.type === "multiple_choice" || que?.type === "checkbox" || que?.type === "true_false") && (
                      <Grid container spacing={2} sx={{ marginTop: 1 }}>
                        {(que?.options || []).map((opt, i) => (
                          <Grid item lg={6} md={6} sm={12} xs={12} key={i}>
                            <div>
                              <div className=" flex gap-10 justify-items-space">
                                <label className={styles.enterque}>Option {i + 1}<span className="input-required"> *</span></label>
                                {que?.options?.length > 1 && que?.type !== "true_false" ? (
                                  <Icon onClick={() => removeAdditionalChoice(i, queInd)} className="delete-icon" icon="ooui:close" width="20" height="20" />
                                ) : null}
                              </div>
                              <div className={styles.textandimgmain}>
                                <div className={styles.textandimg}>
                                  <input
                                    type="text"
                                    placeholder="Enter option"
                                    value={typeof opt === "object" ? opt?.text : opt}
                                    disabled={que?.type === "true_false"}
                                    onChange={(e) => handleMultipleChoiceChange(e, i, queInd)}
                                  />
                                </div>
                              </div>
                              <div className="text-align-left" style={{ marginTop: 5 }}>
                                <div className={styles.correctcontainer}>
                                  <input
                                    type={que?.type === "checkbox" ? "checkbox" : "radio"}
                                    name={que?.type === "checkbox" ? `answer-checkbox-${queInd}` : `answer-${queInd}`}
                                    value={i}
                                    checked={que?.type === "checkbox" ? (que?.answer || []).includes(i) : (que?.answer || [])[0] === i}
                                    onChange={(e) => handleCorrectChoiceChange(e, i, queInd)}
                                  />
                                  <label className={styles.correctlabel} htmlFor="Is Correct">is correct</label>
                                </div>
                              </div>
                            </div>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    {que?.type === "multiple_choice" && (
                      <div className={styles.addoption} onClick={() => handleAdditionalOptions(queInd)}>
                        <Icon icon="octicon:feed-plus-16" width="14" height="14" />
                        <p>Add another option</p>
                      </div>
                    )}
                    {que?.type === "checkbox" && (
                      <div className={styles.addoption} onClick={() => handleAdditionalOptions(queInd)}>
                        <Icon icon="octicon:feed-plus-16" width="14" height="14" />
                        <p>Add another checkbox</p>
                      </div>
                    )}
                    <div className={styles.questionfooter}>
                      <div className="flex gap-10 align-items-center justifyRight">
                        {!que?.description && (
                          <IconButton
                            aria-label="more"
                            onClick={(e) => handleClick(e, queInd)}
                          >
                            <Icon icon="radix-icons:dots-vertical" width="20" height="20" />
                            <label className={styles.enterque}>More</label>
                          </IconButton>
                        )}
                      </div>
                    </div>
                    <Menu anchorEl={anchorEl} open={open} onClose={handleAdditionalQueClose}>
                      <MenuItem
                        onClick={() => {
                          toggleQueDescriptionTrue();
                          handleAdditionalQueClose();
                        }}
                      >
                        Add Description on Question
                      </MenuItem>
                    </Menu>
                  </div>
                </Grid>
              </>
            ))}
          </Grid>
        </div>
        <div className={styles.addque}>
          <Icon icon="octicon:feed-plus-16" width="16" height="16" />
          <p onClick={handleAdditionalQuestion}>Add question</p>
        </div>
        <div>
          <div className={styles.btnwrapper}>
            <Button
              variant="primaryoutline"
              buttonSize="fluid"
              color="btnfontprimary"
              onClick={handleCreateDraftQuestions}
              buttonName="Save as Draft"
            />
            <Button
              variant="primary"
              buttonSize="fluid"
              color="btndefault"
              buttonName="Save to Bank"
              onClick={handleSaveNewQuestions}
            />
          </div>
        </div>
      </>
    );
  }

  function renderEditQuestionBuilder() {
    return (
      <>
        <div className="margin-top">
          <Grid container spacing={2}>
            {editQuestionArr?.map((que, queInd) => (
              <Grid item lg={12} md={12} sm={12} xs={12} key={queInd}>
                <div className={styles.assignmentsection}>
                  <Grid container spacing={2}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <div className={styles.quesaction}>
                        <label className={styles.quelabel}>Enter Question {queInd + 1}</label>
                      </div>
                      <div className={styles.textandimgmain}>
                        <div className={styles.textandimg}>
                          <input
                            type="text"
                            placeholder="Type Question"
                            name="question_text"
                            value={que?.question_text ?? ""}
                            onChange={(e) => editHandleQueChange(e, queInd)}
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <SelectGroup
                        label="Add Answer Type"
                        required
                        children={
                          <select
                            name="type"
                            value={que?.type ?? ""}
                            onChange={(e) => editHandleQueTypeChange(e, queInd)}
                          >
                            <option value="">--- Select ---</option>
                            {questionTypeOptions?.map((label) => (
                              <option key={label?.value} value={label?.value}>{label?.text}</option>
                            ))}
                          </select>
                        }
                      />
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <InputGroup
                        className={styles.pointsInput}
                        label="Question Point"
                        inputType="number"
                        inputName="grade_point"
                        placeHolder="Enter Question Points"
                        value={que?.grade_point ?? ""}
                        onChange={(e) => editHandleQuePointChange(e, queInd)}
                      />
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      {"description" in que && que?.description != null ? (
                        <div className="margin-top full-width">
                          <div className=" flex gap-10 justify-items-space">
                            <label className={styles.enterque}>Question Description</label>
                            <div className="delete-icon" onClick={() => editRemoveQueDescription(queInd)}>
                              <Icon icon="ooui:close" width="20" height="20" />
                            </div>
                          </div>
                          <div className={styles.textandimgmain}>
                            <input
                              type="text"
                              placeholder="Enter Description"
                              name="description"
                              value={que?.description ?? ""}
                              onChange={(e) => editHandleQueDescChange(e, queInd)}
                            />
                          </div>
                        </div>
                      ) : null}
                    </Grid>
                  </Grid>
                  {(que?.type === "multiple_choice" || que?.type === "checkbox" || que?.type === "true_false") && (
                    <Grid container spacing={2} sx={{ marginTop: 1 }}>
                      {(que?.options || []).map((opt, i) => (
                        <Grid item lg={6} md={6} sm={12} xs={12} key={i}>
                          <div>
                            <div className=" flex gap-10 justify-items-space">
                              <label className={styles.enterque}>Option {i + 1}<span className="input-required"> *</span></label>
                              {que?.options?.length > 1 && que?.type !== "true_false" ? (
                                <Icon onClick={() => removeEditChoice(i, queInd)} className="delete-icon" icon="ooui:close" width="20" height="20" />
                              ) : null}
                            </div>
                            <div className={styles.textandimgmain}>
                              <div className={styles.textandimg}>
                                <input
                                  type="text"
                                  placeholder="Enter option"
                                  value={typeof opt === "object" ? (opt?.text ?? "") : (opt ?? "")}
                                  onChange={(e) => editHandleMultipleChoiceChange(e, i, queInd)}
                                  disabled={que?.type === "true_false"}
                                />
                              </div>
                            </div>
                            <div className="text-align-left" style={{ marginTop: 5 }}>
                              <div className={styles.correctcontainer}>
                                <input
                                  type={que?.type === "checkbox" ? "checkbox" : "radio"}
                                  name={que?.type === "checkbox" ? "edit-answer" : `edit-answer-${queInd}`}
                                  value={i}
                                  checked={que?.type === "checkbox" ? (que?.answer || []).includes(i) : (que?.answer || [])[0] === i}
                                  onChange={(e) => editHandleCorrectChoiceChange(e, i, queInd)}
                                />
                                <label className={styles.correctlabel}>is correct</label>
                              </div>
                            </div>
                          </div>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  {que?.type === "multiple_choice" && (
                    <div className={styles.addoption} onClick={() => editHandleAdditionalOptions(queInd)}>
                      <Icon icon="octicon:feed-plus-16" width="14" height="14" />
                      <p>Add another option</p>
                    </div>
                  )}
                  {que?.type === "checkbox" && (
                    <div className={styles.addoption} onClick={() => editHandleAdditionalOptions(queInd)}>
                      <Icon icon="octicon:feed-plus-16" width="14" height="14" />
                      <p>Add another checkbox</p>
                    </div>
                  )}
                  <div className={styles.questionfooter}>
                    {!que?.description && (
                      <IconButton aria-label="more" onClick={(e) => editHandleClick(e, queInd)}>
                        <Icon icon="radix-icons:dots-vertical" width="20" height="20" />
                        <label className={styles.enterque}>More</label>
                      </IconButton>
                    )}
                  </div>
                  <Menu anchorEl={editAnchorEl} open={editMenuOpen} onClose={editHandleMenuClose}>
                    <MenuItem onClick={() => {
                      editToggleQueDescriptionTrue();
                      editHandleMenuClose();
                    }}
                    >Add Description on Question
                    </MenuItem>
                  </Menu>
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
        <div className={styles.btnwrapper}>
          <Button
            variant="primaryoutline"
            buttonSize="fluid"
            color="btnfontprimary"
            buttonName="Cancel"
            onClick={() => setIsEditQuestionsModalOpen(false)}
          />
          <Button
            variant="primary"
            buttonSize="fluid"
            color="btndefault"
            buttonName="Update Question"
            onClick={handleSaveEditQuestion}
          />
        </div>
      </>
    );
  }

  return (
    <Layout
      children={
        <>
          <div className="breadcrumb-container">
            <div>
              <BreadCrumb
                link_one="/school-examination/examination"
                link_one_name="Examination"
                active="Practice Questions"
                description="See all the questions that can be reused"
              />
            </div>
          </div>
          <div className="page-content">
            <h2 style={{ textAlign: "left" }}>Practice Questions</h2>
            <div className="flex gap-10 action_div margin-top-5 margin-bottom">
              <TableSearch
                placeholder="Search here..."
                searchTableFunc={handleSearchStudent}
                value={searchKeyword}
              />
              <div className="flex gap-10 ">
                <div>
                  <SelectGroup
                    children={
                      <select onChange={handleExamTypeChange} name="exam_type" value={selectedExamType}>
                        <option value="">---Select Exam Type---</option>
                        {examTypesData?.map((type, idx) => (
                          <option key={idx} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    }
                  />
                </div>
                <div>
                  <SelectGroup
                    children={
                      <select onChange={handleSubjectChange} name="subject" value={selectedSubject} disabled={!selectedExamType}>
                        <option value="">---Select Subject---</option>
                        {subjects?.map((sub, idx) => (
                          <option key={idx} value={sub?.subject_slug || sub?.id}>
                            {sub?.subject || sub?.displayName}
                          </option>
                        ))}
                      </select>
                    }
                  />
                </div>
                <div>
                  <SelectGroup
                    children={
                      <select onChange={handleYearChange} name="year" value={selectedYear} disabled={!selectedSubject || years.length === 0}>
                        <option value="">---Select Year---</option>
                        {years?.map((year, idx) => (
                          <option key={idx} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    }
                  />
                </div>
              </div>
            </div>

            {isFetchingQuestions ? (
              <div style={{ minHeight: 280, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 40 }}>
                <Icon icon="svg-spinners:270-ring-with-bg" width="40" height="40" style={{ color: "#858585" }} />
                <p style={{ color: "#667185", fontSize: 16, marginTop: 10 }}>Loading questions...</p>
              </div>
            ) : questions.length === 0 ? (
              <div style={{ minHeight: 280, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 40 }}>
                <p style={{ color: "#667185", fontSize: 16 }}>No questions found.</p>
              </div>
            ) : (
              <>
                <Grid container spacing={2}>
                  {paginatedQuestions.map((item, i) => {
                    const renderedIndex = ((currentPage - 1) * QUESTIONS_PER_PAGE) + i;
                    const questionAnswer = item?.answer ?? [];
                    // eslint-disable-next-line no-unused-vars
                    const questionIndex = questions.findIndex((question) => question?.id === item?.id);

                    return (
                      <Grid item lg={4} md={6} sm={12} xs={12} key={item.id || i}>
                        <div className="question-preview-container" style={{ height: "260px", overflowY: "auto" }}>
                          <div className="flex justify-items-space" style={{ alignItems: "flex-start" }}>
                            <h3 className="question-preview-heading">
                              Question {questionDisplayIndex(item, renderedIndex)}
                            </h3>
                          </div>
                          <div className={styles.question}>
                            <h3 className="question-preview-heading" dangerouslySetInnerHTML={{ __html: item?.question?.text || item?.question_text || (typeof item?.question === "string" ? item.question : "") }} />

                            {(item?.type === "multiple_choice" || item?.type === "true_false" || item?.section_type === "objective") ? (
                              <>
                                {Object.entries(item?.question?.options || item?.options || {}).map(([key, option], optIdx) => (
                                  <div key={optIdx} className="options-preview">
                                    <input type="radio" checked={questionAnswer?.includes(optIdx) || questionAnswer?.includes(key)} name={`qb-q-${item?.id}`} readOnly disabled />
                                    <label dangerouslySetInnerHTML={{ __html: typeof option === "object" ? option?.text || option?.value || "" : option }} />
                                  </div>
                                ))}
                              </>
                            ) : item?.type === "checkbox" ? (
                              <>
                                {item?.description && (
                                  <p style={{ fontSize: 13, color: "#667185", marginBlock: "2px 6px" }}>{item.description}</p>
                                )}
                                {Object.entries(item?.question?.options || item?.options || {}).map(([key, option], optIdx) => (
                                  <div key={optIdx} className="options-preview">
                                    <input type="checkbox" checked={questionAnswer?.includes(optIdx) || questionAnswer?.includes(key)} name={`qb-q-${item?.id}`} readOnly disabled />
                                    <label dangerouslySetInnerHTML={{ __html: typeof option === "object" ? option?.text || option?.value || "" : option }} />
                                  </div>
                                ))}
                              </>
                            ) : null}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                            <span style={{ fontSize: 12, color: "#667185" }}>Points: {item?.grade_point || item?.points || 1}</span>
                            <span style={{ fontSize: 12, color: "#667185", textTransform: "capitalize" }}>
                              {(item?.type || item?.section_type || "")?.replace("_", " ")}
                            </span>
                          </div>
                        </div>
                      </Grid>
                    );
                  })}
                </Grid>
                {totalPages > 1 && (
                  <div className="pagination-container">
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      variant="outlined"
                      shape="rounded"
                      color="primary"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <Modal
            fullScreen
            title="Add questions"
            subtitle="Add questions to the question bank."
            isopen={isAddQuestionModalOpen}
            closeModal={() => {
              setIsAddQuestionModalOpen(false);
              setQuestionArr([]);
            }}
            modalContent={renderQuestionBuilder()}
            modalFooter={null}
          />

          <Modal
            fullScreen
            title="Edit question"
            subtitle="Update the selected question."
            isopen={isEditQuestionsModalOpen}
            closeModal={() => setIsEditQuestionsModalOpen(false)}
            modalContent={renderEditQuestionBuilder()}
            modalFooter={null}
          />

          <Modal
            title="Batch Import Questions"
            subtitle={
              <div>Create multiple questions at the same time by importing a csv or Excel file.</div>
            }
            modalContent={
              <>
                <Grid container spacing={2} className="input-padding">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <p className="key-pair-name">1. Choose file type (CSV or Excel) and download the sample file.</p>
                    <p className="key-pair-name">2. Columns: questions, description, answer_type, options, answer, question_bank.</p>
                    <p className="key-pair-name">3. options = text separated by commas; answer = correct option(s); answer_type = multiple_choice or checkbox; question_bank = true or false.</p>
                    <p className="key-pair-name">4. Upload the completed document, then the questions will be added to the bank.</p>
                  </Grid>
                  <BulkQuestionImport
                    fileType={batchFileType}
                    onFileTypeChange={setBatchFileType}
                    createClick={(file) => handleBatchImportQuestions(file)}
                    isLoading={isBatchImportLoading}
                    sampleCsvUrl={questionCsvUrl}
                    sampleXlsxUrl={questionsXlsxUrl}
                  />
                </Grid>
              </>
            }
            modalFooter={<></>}
            isopen={isBatchImportModalOpen}
            closeModal={handleBatchImportModalToggle}
          />

          <Modal
            title="Delete Question"
            subtitle="Are you sure you want to delete this question? This action cannot be undone."
            isopen={isDeleteModalOpen}
            closeModal={handleDeleteModalClose}
            modalContent={<div />}
            modalFooter={
              <div className="flex gap-10">
                <Button
                  variant="dangeroutline"
                  buttonSize="full"
                  color="btndanger"
                  buttonName="Cancel"
                  onClick={handleDeleteModalClose}
                />
                <Button
                  variant="danger"
                  buttonSize="full"
                  color="btndefault"
                  buttonName="Delete"
                  onClick={handleConfirmDelete}
                />
              </div>
            }
          />
        </>
      }
    />
  );
};

export default QuestionBank;
